import api from './api';
import {
  Offer,
  CreateOfferRequest,
  UpdateOfferRequest,
  OfferStatus,
  TransitionStatusRequest,
  SendOfferRequest,
  OfferEmail,
  OfferDocument,
} from '../types';

export const offerService = {
  getAll: async (params?: { clientId?: number; projectId?: number; status?: OfferStatus; includeInactive?: boolean }): Promise<Offer[]> => {
    const response = await api.get('/offers', { params });
    return response.data;
  },

  getById: async (id: number): Promise<Offer> => {
    const response = await api.get(`/offers/${id}`);
    return response.data;
  },

  create: async (data: CreateOfferRequest): Promise<Offer> => {
    const response = await api.post('/offers', data);
    return response.data;
  },

  update: async (id: number, data: UpdateOfferRequest): Promise<Offer> => {
    const response = await api.put(`/offers/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/offers/${id}`);
  },

  nextNumber: async (): Promise<string> => {
    const response = await api.get('/offers/next-number');
    return response.data.number;
  },

  transitionStatus: async (id: number, status: OfferStatus): Promise<Offer> => {
    const body: TransitionStatusRequest = { status };
    const response = await api.post(`/offers/${id}/transition`, body);
    return response.data;
  },

  /** Lädt das gerenderte Angebot-HTML als String (für iframe srcdoc / window.print). */
  renderHtml: async (id: number): Promise<string> => {
    const response = await api.get(`/offers/${id}/render`, { responseType: 'text' });
    return response.data;
  },

  renderUsageAgreementHtml: async (id: number): Promise<string> => {
    const response = await api.get(`/offers/${id}/render/usage-agreement`, { responseType: 'text' });
    return response.data;
  },

  renderAvvHtml: async (id: number): Promise<string> => {
    const response = await api.get(`/offers/${id}/render/avv`, { responseType: 'text' });
    return response.data;
  },

  renderAvvPdf: async (id: number): Promise<Blob> => {
    const response = await api.get(`/offers/${id}/render/avv/pdf`, { responseType: 'blob' });
    return response.data;
  },

  renderPdf: async (id: number): Promise<Blob> => {
    const response = await api.get(`/offers/${id}/render/pdf`, { responseType: 'blob' });
    return response.data;
  },

  renderUsageAgreementPdf: async (id: number): Promise<Blob> => {
    const response = await api.get(`/offers/${id}/render/usage-agreement/pdf`, { responseType: 'blob' });
    return response.data;
  },

  /** Verschickt das Angebot per E-Mail; ein Entwurf wird dabei auf „Versendet“ gesetzt. */
  send: async (id: number, data: SendOfferRequest): Promise<Offer> => {
    const response = await api.post(`/offers/${id}/send`, data);
    return response.data;
  },

  emails: async (id: number): Promise<OfferEmail[]> => {
    const response = await api.get(`/offers/${id}/emails`);
    return response.data;
  },

  /** Archivkopie eines Versands (Angebot, Nutzungsvereinbarung oder AVV), genau wie verschickt. */
  emailPdf: async (id: number, emailId: number, document: OfferDocument = 'OFFER'): Promise<Blob> => {
    const response = await api.get(`/offers/${id}/emails/${emailId}/pdf`, {
      params: { document },
      responseType: 'blob',
    });
    return response.data;
  },
};

export default offerService;
